import { memo, type ComponentType } from 'react';

import { CancerTypeFilter } from './CancerTypeFilter';
import { MobileAllTab } from './MobileAllTab';
import { MobileClosedTab } from './MobileClosedTab';
import { MobileDraftsTab } from './MobileDraftsTab';
import { MobileOpenTab } from './MobileOpenTab';
import { NewSupportMessageFab } from './NewSupportMessageFab';
import { type MobileInboxProps, type SceneProps } from './types';

import { SegmentTabs } from '@/components/SegmentTabs';
import { TabRootScenes } from '@/components/TabRootList';
import { VStack } from '@/components/ui/vstack';

const SCENES: Record<string, ComponentType<SceneProps>> = {
  all: MobileAllTab,
  open: MobileOpenTab,
  drafts: MobileDraftsTab,
  closed: MobileClosedTab,
};

/**
 * The phone inbox: one list of inquiries at a time, picked by the segment control at the top
 * of it.
 *
 * The banners, the cancer type filter and the segment control all render inside the list
 * rather than above it, so the native header's large title can collapse as the list scrolls.
 */
export const MobileInbox = memo(function MobileInbox({
  selectedCancerFocus,
  inquiryCancerTypesQuery,
  tabState,
  onIndexChange,
  setSelectedCancerFocus,
  resetSelectedChatId,
  leadBanners,
}: MobileInboxProps) {
  const selectedKey = tabState.routes[tabState.index]?.key ?? 'open';

  const lead = (
    <>
      {leadBanners}

      <CancerTypeFilter
        inquiryCancerTypesData={inquiryCancerTypesQuery.data}
        selectedCancerFocus={selectedCancerFocus}
        setSelectedCancerFocus={setSelectedCancerFocus}
        resetSelectedChatId={resetSelectedChatId}
        disabled={selectedKey === 'drafts'}
      />

      <SegmentTabs
        tabs={tabState.routes}
        selectedKey={selectedKey}
        onSelect={(key) => onIndexChange(tabState.routes.findIndex((route) => route.key === key))}
      />
    </>
  );

  return (
    <VStack className="w-full flex-1">
      <TabRootScenes
        sceneKeys={tabState.routes.map((route) => route.key)}
        selectedKey={selectedKey}
        lead={lead}
        renderScene={(key) => {
          const Scene = SCENES[key];

          return Scene ? <Scene route={{ params: { selectedCancerFocus } }} /> : null;
        }}
      />

      <NewSupportMessageFab />
    </VStack>
  );
});
